import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { HistoryEntry } from '@rbv/shared';

dayjs.extend(relativeTime);

type EntriesState = 'idle' | 'loading' | 'unavailable' | HistoryEntry[];

interface Preview {
  hash: string;
  message: string;
  /** null while loading. */
  content: string | null;
  failed?: boolean;
}

/** Per-note git version history (Phase 2). Collapsed by default; the server
 * answers 501 until the history backend is registered. */
export function HistoryPanel({ noteId }: { noteId: string }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<EntriesState>('idle');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    setOpen(false);
    setEntries('idle');
    setPreview(null);
    setNotice(null);
  }, [noteId]);

  const load = useCallback(async () => {
    setEntries('loading');
    try {
      const res = await fetch(`/api/history/${encodeURIComponent(noteId)}`);
      if (!res.ok) {
        setEntries('unavailable');
        return;
      }
      const data = (await res.json()) as unknown;
      setEntries(Array.isArray(data) ? (data as HistoryEntry[]) : 'unavailable');
    } catch {
      setEntries('unavailable');
    }
  }, [noteId]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && entries === 'idle') void load();
  };

  const openPreview = async (e: HistoryEntry) => {
    setPreview({ hash: e.hash, message: e.message, content: null });
    try {
      const res = await fetch(
        `/api/history/${encodeURIComponent(noteId)}/${encodeURIComponent(e.hash)}`,
      );
      if (!res.ok) throw new Error(String(res.status));
      const d = (await res.json()) as { content: string };
      setPreview((p) => (p && p.hash === e.hash ? { ...p, content: d.content } : p));
    } catch {
      setPreview((p) =>
        p && p.hash === e.hash
          ? { ...p, content: 'Could not load this version.', failed: true }
          : p,
      );
    }
  };

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice((n) => (n === msg ? null : n)), 4000);
  };

  const restore = async () => {
    if (!preview) return;
    if (!confirm('Restore this version? The current note content will be replaced.')) return;
    try {
      const res = await fetch(`/api/history/${encodeURIComponent(noteId)}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hash: preview.hash }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setPreview(null);
      flash('Version restored.');
      void load();
    } catch {
      flash('Restore failed.');
    }
  };

  return (
    <section className="panel-section">
      <button className="panel-header collapsible" onClick={toggle}>
        <span className={`tree-chevron${open ? ' open' : ''}`}>▸</span>
        <span className="panel-title">Version history</span>
        {Array.isArray(entries) && <span className="count-badge">{entries.length}</span>}
      </button>

      {notice && <div className="inline-notice">{notice}</div>}

      {open && entries === 'loading' && <div className="spinner" />}
      {open && entries === 'unavailable' && (
        <div className="pane-empty">History unavailable on this server.</div>
      )}
      {open && Array.isArray(entries) && entries.length === 0 && (
        <div className="pane-empty">No recorded versions yet.</div>
      )}
      {open && Array.isArray(entries) && entries.length > 0 && (
        <div className="hist-list">
          {entries.map((e) => (
            <button
              key={e.hash}
              className="hist-row"
              title={new Date(e.date).toLocaleString()}
              onClick={() => void openPreview(e)}
            >
              <span className="hist-msg">{e.message || e.hash.slice(0, 7)}</span>
              <span className="hist-date">{dayjs(e.date).fromNow()}</span>
            </button>
          ))}
        </div>
      )}

      {preview && (
        <div className="dialog-backdrop" onClick={() => setPreview(null)}>
          <div className="dialog hist-dialog" onClick={(ev) => ev.stopPropagation()}>
            <div className="dialog-title">{preview.message || preview.hash.slice(0, 7)}</div>
            {preview.content === null ? (
              <div className="spinner" />
            ) : (
              <pre className="hist-content">{preview.content}</pre>
            )}
            <div className="dialog-actions">
              <button className="dlg-btn" onClick={() => setPreview(null)}>
                Close
              </button>
              <button
                className="dlg-btn primary"
                disabled={preview.content === null || preview.failed === true}
                onClick={() => void restore()}
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
